import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { getArtists, getArtist, getArtistsStats, getSimilarArtists, getArtistSuggestions } from '../api/artists'
import { ArtistFilters } from '../types/artist'

export const artistsKeys = {
  all: ['artists'] as const,
  list: (filters: ArtistFilters) => [...artistsKeys.all, 'list', filters] as const,
  detail: (id: number) => [...artistsKeys.all, 'detail', id] as const,
  stats: () => [...artistsKeys.all, 'stats'] as const,
  similarToRelease: (releaseId: number) => [...artistsKeys.all, 'similar-to-release', releaseId] as const,
}

export function useArtists(filters: ArtistFilters = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: artistsKeys.list(filters),
    queryFn: () => getArtists(filters),
    enabled: options?.enabled !== false,
  })
}

export function useArtist(id: number) {
  return useQuery({
    queryKey: artistsKeys.detail(id),
    queryFn: () => getArtist(id),
    enabled: !!id,
  })
}

export function useArtistsStats() {
  return useQuery({
    queryKey: artistsKeys.stats(),
    queryFn: () => getArtistsStats(),
  })
}

export function useArtistSuggestions(q: string, debounceMs = 200) {
  const [debounced, setDebounced] = useState(q)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), debounceMs)
    return () => clearTimeout(t)
  }, [q, debounceMs])

  return useQuery({
    queryKey: [...artistsKeys.all, 'suggest', debounced] as const,
    queryFn: () => getArtistSuggestions(debounced),
    enabled: debounced.trim().length >= 1,
    staleTime: 1000 * 30,
    placeholderData: [],
  })
}

export function useSimilarArtists(releaseId: number, limit = 10) {
  return useQuery({
    queryKey: artistsKeys.similarToRelease(releaseId),
    queryFn: () => getSimilarArtists(releaseId, limit),
    enabled: !!releaseId,
    staleTime: 1000 * 60 * 60,
  })
}
